import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiArrowLongDown } from "react-icons/hi2";
import { RiGithubFill } from "react-icons/ri";
import { Link } from "react-router-dom";

import hero_fallback_bg from "../../assets/background/hero_fallback_bg.png";
import hero_video from "../../assets/videos/hero_video.mp4";
import hero_video_low_res from "../../assets/videos/hero_video_low_res.mp4";

import useInView from "../../hooks/useInView";
import { MAIN_ROUTES } from "../../utils/routes";

const Hero = () => {
  const { t } = useTranslation("home");
  const [setRef] = useInView({
    threshold: 0.1,
  });
  const containerRef = useRef(null);
  const [mouseOver, setMouseOver] = useState(false);

  const handleOnClickRequest = () => {
    document
      .querySelector(`#${MAIN_ROUTES.get("OVERVIEW").ID}`)
      ?.scrollIntoView({
        behavior: "smooth",
      });
  };

  return (
    <section
      className="relative flex min-h-[calc(100vh-64px)] w-full items-center overflow-hidden bg-sky-50"
      id={MAIN_ROUTES.get("HOME").ID}
      ref={setRef}
    >
      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-64px)] w-full max-w-8xl items-center px-4 py-16 sm:px-6 lg:px-8">
        <div
          className="relative flex w-full justify-center lg:justify-start"
          ref={containerRef}
        >
          <div className="flex max-w-3xl flex-col items-center gap-10 text-center lg:items-start lg:text-left">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-3">
                <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-100 sm:text-base">
                  {t("hero.subheader")}
                </p>
                <h1 className="text-6xl font-semibold tracking-tight text-white sm:text-6xl lg:text-8xl">
                  {t("hero.header")}
                </h1>
              </div>

              <p className="max-w-2xl text-base leading-6 text-slate-200 sm:text-base lg:text-xl">
                {t("hero.content")}
              </p>
            </div>

            <div className="flex w-full flex-col items-center gap-4 sm:w-auto sm:flex-row lg:items-start">
              <button
                className="group flex w-full items-center justify-center gap-3 overflow-hidden rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-slate-200 sm:w-auto"
                onClick={handleOnClickRequest}
                onMouseEnter={() => setMouseOver(true)}
                onMouseLeave={() => setMouseOver(false)}
                type="button"
              >
                <span>{t("hero.action")}</span>
                <span className="relative flex h-5 w-4 overflow-hidden">
                  <HiArrowLongDown
                    className="absolute left-0 top-0 text-lg transition duration-300"
                    style={{
                      transform: mouseOver
                        ? "translateY(-26px)"
                        : "translateY(-1px)",
                    }}
                  />
                  <HiArrowLongDown
                    className="absolute left-0 top-0 text-lg transition duration-300"
                    style={{
                      transform: mouseOver
                        ? "translateY(1px)"
                        : "translateY(26px)",
                    }}
                  />
                </span>
              </button>

              <Link
                className="flex w-full items-center justify-center gap-3 rounded-full bg-sky-500 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-600 sm:w-auto"
                rel="noopener noreferrer"
                target="_blank"
                to={MAIN_ROUTES.get("GITHUB").PATH}
              >
                <RiGithubFill className="text-2xl" />
                <span>{t("hero.subaction")}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(14,143,247,0.3),_transparent_42%),linear-gradient(135deg,rgba(7,19,43,0.72),rgba(17,63,99,0.78))]" />
        <div className="absolute inset-0 right-[-8%] md:w-[130%] lg:right-0 lg:w-full">
          <video
            autoPlay
            id="_hero_video"
            loading="lazy"
            loop
            muted
            poster={hero_fallback_bg}
            preload="none"
            className="h-full w-full object-cover brightness-[0.45] contrast-[1.2] hue-rotate-[32deg]"
          >
            <source media="(max-width: 640px)" src={hero_video_low_res} type="video/mp4" />
            <source src={hero_video} type="video/mp4" />
          </video>
        </div>
      </div>
    </section>
  );
};

export default Hero;
